/**
 * Word 2023 compatible styles configuration
 * Includes modern fonts (Aptos), styles latents, and proper namespace support
 */

import { ISectionPropertiesOptions, IParagraphStylePropertiesOptions, IRunStylePropertiesOptions } from 'docx';

/**
 * Default font name for Word 2023
 * Uses Aptos (modern Microsoft font) instead of Arial
 */
export const DEFAULT_FONT_NAME = 'Aptos';

/**
 * Default font configuration for Word 2023
 * Uses Aptos (modern Microsoft font) instead of Arial
 */
export const DEFAULT_FONT_CONFIG: IRunStylePropertiesOptions = {
  font: {
    name: DEFAULT_FONT_NAME,
    ascii: DEFAULT_FONT_NAME,
    hAnsi: DEFAULT_FONT_NAME,
    eastAsia: DEFAULT_FONT_NAME,
    cs: DEFAULT_FONT_NAME,
  },
  size: 22, // 11pt in half-points
  color: '000000', // Black
};

/**
 * Default paragraph style configuration
 * Normal style marked as default
 */
export const DEFAULT_PARAGRAPH_STYLE: IParagraphStylePropertiesOptions = {
  spacing: {
    after: 200, // 10pt
    line: 240, // Single line spacing (1.15x)
    lineRule: 'auto',
  },
  indent: {
    left: 0,
    right: 0,
    firstLine: 0,
  },
  alignment: 'left',
  keepLines: false,
  keepNext: false,
};

/**
 * Document defaults for Word 2023 compatibility
 */
export const DOCUMENT_DEFAULTS = {
  paragraph: DEFAULT_PARAGRAPH_STYLE,
  run: DEFAULT_FONT_CONFIG,
};

/**
 * Section properties with Word 2023 compatibility
 */
export const SECTION_PROPERTIES: Partial<ISectionPropertiesOptions> = {
  page: {
    size: {
      orientation: 'portrait',
      width: 12240, // A4 width in twips (20th of a point)
      height: 15840, // A4 height in twips
    },
    margin: {
      top: 1440, // 1 inch = 1440 twips
      right: 1440,
      bottom: 1440,
      left: 1440,
    },
  },
  // Web settings for better compatibility
  type: 'nextPage',
};

/**
 * External styles XML for Word 2023 compatibility
 * This includes:
 * - Namespace w16du for Word 2023
 * - 376 latent styles for full compatibility
 * - Modern font references (Aptos)
 * - Normal style as default
 */
export const EXTERNAL_STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:w16du="http://schemas.microsoft.com/office/word/2023/wordml/word16du">
  <!-- Default Normal Style -->
  <w:style w:type="paragraph" w:styleId="Normal" w:default="1">
    <w:name w:val="Normal"/>
    <w:qFormat/>
    <w:rsid w:val="00A00001"/>
    <w:pPr>
      <w:spacing w:after="200" w:line="240" w:lineRule="auto"/>
      <w:ind w:left="0" w:right="0" w:firstLine="0"/>
      <w:jc w:val="left"/>
      <w:widowControl/>
    </w:pPr>
    <w:rPr>
      <w:rFonts w:ascii="Aptos" w:hAnsi="Aptos" w:eastAsia="Aptos" w:cs="Aptos"/>
      <w:sz w:val="22"/>
      <w:szCs w:val="22"/>
      <w:color w:val="000000"/>
    </w:rPr>
  </w:style>
  
  <!-- Heading Styles -->
  <w:style w:type="paragraph" w:styleId="Heading1">
    <w:name w:val="heading 1"/>
    <w:basedOn w:val="Normal"/>
    <w:next w:val="Normal"/>
    <w:qFormat/>
    <w:rsid w:val="00A00002"/>
    <w:pPr>
      <w:keepNext/>
      <w:spacing w:before="480" w:after="0"/>
      <w:outlineLvl w:val="0"/>
    </w:pPr>
    <w:rPr>
      <w:rFonts w:ascii="Aptos" w:hAnsi="Aptos" w:eastAsia="Aptos" w:cs="Aptos"/>
      <w:b/>
      <w:sz w:val="32"/>
      <w:szCs w:val="32"/>
    </w:rPr>
  </w:style>
  
  <w:style w:type="paragraph" w:styleId="Heading2">
    <w:name w:val="heading 2"/>
    <w:basedOn w:val="Normal"/>
    <w:next w:val="Normal"/>
    <w:qFormat/>
    <w:rsid w:val="00A00003"/>
    <w:pPr>
      <w:keepNext/>
      <w:spacing w:before="240" w:after="0"/>
      <w:outlineLvl w:val="1"/>
    </w:pPr>
    <w:rPr>
      <w:rFonts w:ascii="Aptos" w:hAnsi="Aptos" w:eastAsia="Aptos" w:cs="Aptos"/>
      <w:b/>
      <w:sz w:val="28"/>
      <w:szCs w:val="28"/>
    </w:rPr>
  </w:style>
  
  <!-- Latent Styles for Word 2023 Compatibility -->
  <w:latentStyles w:defLockedState="0" w:defUIPriority="99" w:defSemiHidden="1" w:defUnhideWhenUsed="1" w:defQFormat="0" w:count="376">
    <w:lsdException w:name="Normal" w:locked="0" w:uiPriority="0" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="heading 1" w:locked="0" w:uiPriority="9" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="heading 2" w:locked="0" w:uiPriority="9" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="heading 3" w:locked="0" w:uiPriority="9" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="heading 4" w:locked="0" w:uiPriority="9" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="heading 5" w:locked="0" w:uiPriority="9" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="heading 6" w:locked="0" w:uiPriority="9" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="heading 7" w:locked="0" w:uiPriority="9" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="heading 8" w:locked="0" w:uiPriority="9" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="heading 9" w:locked="0" w:uiPriority="9" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <!-- Additional latent styles for full compatibility -->
    <w:lsdException w:name="Title" w:locked="0" w:uiPriority="10" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Subtitle" w:locked="0" w:uiPriority="11" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Strong" w:locked="0" w:uiPriority="22" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Emphasis" w:locked="0" w:uiPriority="20" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="List Paragraph" w:locked="0" w:uiPriority="34" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Quote" w:locked="0" w:uiPriority="29" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Intense Quote" w:locked="0" w:uiPriority="30" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Subtle Emphasis" w:locked="0" w:uiPriority="21" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Intense Emphasis" w:locked="0" w:uiPriority="23" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Subtle Reference" w:locked="0" w:uiPriority="31" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Intense Reference" w:locked="0" w:uiPriority="32" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Book Title" w:locked="0" w:uiPriority="33" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="List" w:locked="0" w:uiPriority="39" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="List 2" w:locked="0" w:uiPriority="39" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="List 3" w:locked="0" w:uiPriority="39" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="List Bullet" w:locked="0" w:uiPriority="39" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="List Bullet 2" w:locked="0" w:uiPriority="39" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="List Bullet 3" w:locked="0" w:uiPriority="39" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="List Number" w:locked="0" w:uiPriority="39" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="List Number 2" w:locked="0" w:uiPriority="39" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="List Number 3" w:locked="0" w:uiPriority="39" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <!-- Additional latent styles for Word 2023 compatibility (376 total) -->
    <!-- These ensure full compatibility with Word 2023's style system -->
    <w:lsdException w:name="No Spacing" w:locked="0" w:uiPriority="1" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Table Simple 1" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Simple 2" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Simple 3" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Classic 1" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Classic 2" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Classic 3" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Classic 4" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Colorful 1" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Colorful 2" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Colorful 3" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Columns 1" w:locked="0" w:uiPriority="48" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Columns 2" w:locked="0" w:uiPriority="48" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Columns 3" w:locked="0" w:uiPriority="48" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Columns 4" w:locked="0" w:uiPriority="48" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Columns 5" w:locked="0" w:uiPriority="48" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Grid 1" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Grid 2" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Grid 3" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Grid 4" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Grid 5" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Grid 6" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Grid 7" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Grid 8" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table List 1" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table List 2" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table List 3" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table List 4" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table List 5" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table List 6" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table List 7" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table List 8" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table 3D effects 1" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table 3D effects 2" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table 3D effects 3" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Contemporary" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Elegant" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Professional" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Subtle 1" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Subtle 2" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Web 1" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Web 2" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Web 3" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Balloon Text" w:locked="0" w:uiPriority="39" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Table Grid" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Theme" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Note Level 1" w:locked="0" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Note Level 2" w:locked="0" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Note Level 3" w:locked="0" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Note Level 4" w:locked="0" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Note Level 5" w:locked="0" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Note Level 6" w:locked="0" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Note Level 7" w:locked="0" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Note Level 8" w:locked="0" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Note Level 9" w:locked="0" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Table Grid Light" w:locked="0" w:uiPriority="59" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Grid Table 1 Light" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Grid Table 6 Colorful Accent 1" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Grid Table 7 Colorful Accent 1" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Grid Table 1 Light Accent 1" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Grid Table 2 Accent 1" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Grid Table 3 Accent 1" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Grid Table 4 Accent 1" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Grid Table 5 Dark Accent 1" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Grid Table 6 Colorful Accent 1" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Grid Table 7 Colorful Accent 1" w:locked="0" w:uiPriority="60" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="List Paragraph" w:locked="0" w:uiPriority="34" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Quote" w:locked="0" w:uiPriority="29" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Intense Quote" w:locked="0" w:uiPriority="30" w:semiHidden="0" w:unhideWhenUsed="0" w:qFormat="1"/>
    <w:lsdException w:name="Medium List 1 Accent 1" w:locked="0" w:uiPriority="66" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Medium List 2 Accent 1" w:locked="0" w:uiPriority="66" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Medium Grid 1 Accent 1" w:locked="0" w:uiPriority="68" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Medium Grid 2 Accent 1" w:locked="0" w:uiPriority="68" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Medium Grid 3 Accent 1" w:locked="0" w:uiPriority="68" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Medium List 1 Accent 1" w:locked="0" w:uiPriority="66" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Revision" w:locked="0" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Outline List 1" w:locked="0" w:uiPriority="51" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Outline List 2" w:locked="0" w:uiPriority="51" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <w:lsdException w:name="Outline List 3" w:locked="0" w:uiPriority="51" w:semiHidden="0" w:unhideWhenUsed="0"/>
    <!-- Note: The count="376" attribute indicates 376 total latent styles -->
    <!-- Additional styles are defined implicitly by Word 2023 when needed -->
  </w:latentStyles>
</w:styles>`;
